import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { Formik, Field, Form } from 'formik';
import { Grid } from '@mui/material';
import { useTracking } from 'react-tracking';
import { makeStyles } from 'src/makeStyles';
import { createStructuredSelector } from 'reselect';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import { find } from 'lodash';
import { trackImpression } from 'utils/blitzerTracking';
import { useTranslation, Router, i18n } from '../../../src/i18n';
import route from '../../../constants/routes';
import Button from '../../../components/Button';
import AutocompleteSelect from '../../../components/Fields/AutocompleteSelect';
import { getKeyOfTranslateBySortSlug } from '../utils';
import {
  selectCompanies,
  selectCompaniesLoading,
  selectOperas,
  selectOperasLoading,
  selectTeams,
  selectTeamsLoading,
  selectStartAggregations,
} from '../selectors';
import { PRODUCTION_RENTALS_AGGREGATION_TYPES, TP, RENTALS_SORT_ORDER } from '../../../constants/consts';
import { getRentalsAggregations, setStartAggregationAction, cleanRentalsAggregations } from '../actions';

const useStyles = makeStyles()(theme => ({
  form: {
    display: 'flex',
    width: '100%',
    alignItems: 'flex-end',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  submitButton: {
    marginLeft: '1rem',
    fontSize: '0.9rem',
    fontWeight: 'bold',
    padding: '10px',
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      marginTop: '1rem',
    },
  },
}));

export const rentalsSearchFormFields = {
  opera: {
    id: PRODUCTION_RENTALS_AGGREGATION_TYPES.WORK_ID,
    aggregationType: PRODUCTION_RENTALS_AGGREGATION_TYPES.WORK_ID,
    label: `${TP}.FN_PRODUCTION_CARD_HEADER_WORK`,
  },
  team: {
    id: PRODUCTION_RENTALS_AGGREGATION_TYPES.PRODUCTION_TEAM,
    aggregationType: PRODUCTION_RENTALS_AGGREGATION_TYPES.PRODUCTION_TEAM,
    label: `${TP}.m_PRODTEAM`,
  },
  company: {
    id: PRODUCTION_RENTALS_AGGREGATION_TYPES.COMPANY,
    aggregationType: PRODUCTION_RENTALS_AGGREGATION_TYPES.COMPANY,
    label: `${TP}.m_ALLCOMPANIES`,
  },
};

const { opera, team, company } = rentalsSearchFormFields;

const RentalsSearchForm = ({
  operas,
  operasLoading,
  teams,
  teamsLoading,
  companies,
  companiesLoading,
  startAggregations,
  getRentals,
  initialValues,
  initialSortName,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const tracking = useTracking();
  const sortTypes = RENTALS_SORT_ORDER?.map(({ slug, key }) => ({
    value: slug,
    label: i18n.t(getKeyOfTranslateBySortSlug(key)),
  }));
  const [sortOption, setSortOption] = useState(null);

  React.useEffect(() => {
    if (initialSortName) {
      setSortOption(find(sortTypes, { value: initialSortName }) || sortTypes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSortName]);

  const searchFilter = debounce((value, type) => {
    dispatch(
      getRentalsAggregations({
        query: value,
        type,
        callback: data => {
          data?.forEach((searchedData, index) => {
            tracking.trackEvent(
              trackImpression('rentalsSearchFilterImpression', type, {
                entityId: searchedData?.id,
                entityName: searchedData?.name,
                searchKey: value,
                searchResultPosition: index,
              }),
            );
          });
        },
      }),
    );
  }, 500);

  const onSubmit = values => {
    const query = {
      crew_profile_id: get(values, `${team.id}.value`),
      work_id: get(values, `${opera.id}.value`),
      company_id: get(values, `${company.id}.value`),
      sort: get(sortOption, 'value'),
    };
    Object.keys(query).forEach(key => {
      if (!query[key]) {
        delete query[key];
      }
    });

    Router.push(
      route.RENTALS,
      `${route.RENTALS}?${Object.keys(query)
        .map(qKey => `${qKey}=${query[qKey]}`)
        .join('&')}`,
      { shallow: true },
    );
    getRentals(query, 'form');
  };

  const resetOptions = fieldName => {
    dispatch(cleanRentalsAggregations(fieldName));
  };

  return (
    <Formik initialValues={initialValues} enableReinitialize onSubmit={onSubmit}>
      {({ values }) => (
        <Form className={classes.form} autoComplete="off">
          <Grid container spacing={2} className={classes.formFields}>
            <Grid item sm={3} xs={12}>
              <Field
                className={classes.field}
                key={opera.id}
                name={opera.id}
                label={t(opera.label)}
                component={AutocompleteSelect}
                options={operas}
                onInputTextChange={v => searchFilter(v, opera.aggregationType)}
                onFocus={() => {
                  dispatch(setStartAggregationAction());
                  const { query } = values;
                  return searchFilter(query, opera.aggregationType);
                }}
                resetOptions={resetOptions}
                isLoading={operasLoading}
                instanceId={opera.id}
                startAggregations={startAggregations}
                isClearable
                isEditable
                noIndicator
              />
            </Grid>
            <Grid item sm={3} xs={12}>
              <Field
                className={classes.field}
                key={team.id}
                name={team.id}
                label={t(team.label)}
                component={AutocompleteSelect}
                options={teams}
                onInputTextChange={v => searchFilter(v, team.aggregationType)}
                onFocus={() => {
                  dispatch(setStartAggregationAction());
                  const { query } = values;
                  return searchFilter(query, team.aggregationType);
                }}
                resetOptions={resetOptions}
                isLoading={teamsLoading}
                instanceId={team.id}
                startAggregations={startAggregations}
                isClearable
                isEditable
                noIndicator
              />
            </Grid>
            <Grid item sm={3} xs={12}>
              <Field
                className={classes.field}
                key={company.id}
                name={company.id}
                label={t(company.label)}
                component={AutocompleteSelect}
                options={companies}
                onInputTextChange={v => searchFilter(v, company.aggregationType)}
                onFocus={() => {
                  dispatch(setStartAggregationAction());
                  const { query } = values;
                  return searchFilter(query, company.aggregationType);
                }}
                resetOptions={resetOptions}
                isLoading={companiesLoading}
                instanceId={company.id}
                startAggregations={startAggregations}
                isClearable
                isEditable
                noIndicator
              />
            </Grid>
            <Grid item sm={3} xs={12}>
              <Field
                className={classes.field}
                field={{ name: 'sort', value: sortOption }}
                label={t(`${TP}.m_ASKSORT`)}
                component={AutocompleteSelect}
                options={sortTypes}
                instanceId="sort"
                preventDefaultOnChange
                onChange={setSortOption}
                startAggregations={startAggregations}
                isClearable
                isEditable
              />
            </Grid>
          </Grid>
          <Button className={classes.submitButton} type="submit">
            {t(`${TP}.m_GOBUTTON`)}
          </Button>
        </Form>
      )}
    </Formik>
  );
};

RentalsSearchForm.propTypes = {
  operas: PropTypes.array,
  operasLoading: PropTypes.bool,
  teams: PropTypes.array,
  teamsLoading: PropTypes.bool,
  companies: PropTypes.array,
  companiesLoading: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  operas: selectOperas(),
  operasLoading: selectOperasLoading(),
  teams: selectTeams(),
  teamsLoading: selectTeamsLoading(),
  companies: selectCompanies(),
  companiesLoading: selectCompaniesLoading(),
  startAggregations: selectStartAggregations(),
});

const withConnect = connect(mapStateToProps, null);

export default withConnect(RentalsSearchForm);
